import { Vector2, Vector3, Vector4, type DataTexture, type IUniform } from 'three';
import type { StyleParams, PainterProjectionState } from '../types';
import type { PaletteModel } from '../../data/types';
import { WORLD } from '../../core/config';
import { buildSurfaceShadeRatios, createShadeUniformArrays } from '../surfaceShading';
import { WARP_FADE_DEPTH_M, WARP_LIMIT_PX, type VerticalShiftField } from '../painterWarp';
import { painterWarpCounts, painterWarpTexture } from '../painterWarpTexture';
import { painterWarpLookup } from '../painterWarpLookup';
import { WALL_EXAGGERATION } from '../wallExaggeration';
import { painterStrokeUniformData } from '../painterStrokes';
import { fieldTextureColours } from '../fieldTextureColours';
import { fieldColourUniforms, type FieldColourUniforms } from '../fieldColour';
import { loadFieldColourMap } from '../../data/loadFieldColourMap';

export interface SharedUniforms extends FieldColourUniforms {
  uHazeColor: IUniform<Vector3>;
  uFogDensity: IUniform<number>;
  uFogExponent: IUniform<number>;
  uFogHeightFalloff: IUniform<number>;
  uFogMaxOpacity: IUniform<number>;
  uFogPainterExponent: IUniform<number>;
  uFogCalibrationM: IUniform<number>;
  uSkyStops: IUniform<Vector3[]>;
  uSkyElevations: IUniform<number[]>;
  uSkyStopCount: IUniform<number>;
  uSunDirection: IUniform<Vector3>;
  uWrap: IUniform<number>;
  uBandCount: IUniform<number>;
  uBandSoftness: IUniform<number>;
  uSkyDepthSentinel: IUniform<number>;
  uTime: IUniform<number>;
  uPainterAmount: IUniform<number>;
  uPainterGroundPoint: IUniform<Vector3>;
  uPainterD0: IUniform<number>;
  uPainterL: IUniform<number>;
  uPainterFarKneeM: IUniform<number>;
  uPainterFarKneeShare: IUniform<number>;
  uPainterFarScaleM: IUniform<number>;
  uPainterFarShear: IUniform<number>;
  uVerticalExaggeration: IUniform<number>;
  uPainterCameraHeight: IUniform<number>;
  uPainterCameraPos: IUniform<Vector3>;
  uPainterForwardDir: IUniform<Vector3>;
  uPainterRightDir: IUniform<Vector3>;
  uPainterHorizonDepressionRad: IUniform<number>;
  uPainterHorizonElevationTop: IUniform<number>;
  uPainterEye: IUniform<Vector3>;
  uPainterAxisRight: IUniform<Vector3>;
  uPainterAxisUp: IUniform<Vector3>;
  uPainterAxisForward: IUniform<Vector3>;
  uPainterFocalPx: IUniform<number>;
  uPainterPrincipalPx: IUniform<Vector2>;
  uPainterWarpInvScale: IUniform<number>;
  uPainterWarpAffine: IUniform<Vector3>;
  /* Both splines' centres and weights (painterWarpTexture.ts). */
  uPainterWarpField: IUniform<DataTexture>;
  uPainterWarpLookup: IUniform<DataTexture>;
  uPainterWarpGrid: IUniform<Vector4>;
  /* The citizens' pose table, once their file is loaded (citizenPoses.ts); only a batch of
     citizens reads it. */
  uCitizenPoses: IUniform<DataTexture | null>;
  uPainterWarpFadeDepthM: IUniform<number>;
  uPainterWarpLimitPx: IUniform<number>;
  /* Only for a warp with a column shift, which painterWarpDefines compiles into the shaders. */
  uPainterWarpVerticalInvScale?: IUniform<number>;
  uPainterWarpVerticalHeldRowPx?: IUniform<number>;
  uPainterWarpVerticalLastFittedRowPx?: IUniform<number>;
  uPainterWarpVerticalAffine?: IUniform<Vector3>;
  uWallFarShare: IUniform<number>;
  uWallFacingFrom: IUniform<number>;
  uWallFacingTo: IUniform<number>;
  uPainterStrokeBoxes: IUniform<Vector4[]>;
  uPainterStrokeValues: IUniform<Vector4[]>;
  uPainterStrokeVertices: IUniform<Vector4[]>;
  uShadeLow: IUniform<Float32Array>;
  uShadeHigh: IUniform<Float32Array>;
  uShadeChroma: IUniform<Float32Array>;
  uBrushStrength: IUniform<number>;
  uBrushLengthM: IUniform<number>;
  uBrushWidthM: IUniform<number>;
  uDitchColor: IUniform<Vector3>;
  uFieldToneA: IUniform<Vector3>;
  uFieldToneB: IUniform<Vector3>;
  uFieldStreak: IUniform<Vector3>;
  uHighlightColor: IUniform<Vector3>;
  uRenderHeight: IUniform<number>;
  uCloudColor: IUniform<Vector3>;
  uCloudCoverage: IUniform<number>;
  uCloudScale: IUniform<number>;
}

export function createSharedUniforms(style: StyleParams, palette: PaletteModel): SharedUniforms {
  const atm = style.atmosphere;
  const sky = style.sky;
  const light = style.light;
  const brush = style.brush;

  const skyStops = sky.stops.map((s) => new Vector3(s.color[0], s.color[1], s.color[2]));
  while (skyStops.length < 8) {
    skyStops.push(new Vector3(0, 0, 0));
  }

  const skyElevations = sky.stops.map((s) => s.elevationDeg);
  while (skyElevations.length < 8) {
    skyElevations.push(90);
  }

  const pp = style.painterProjection;
  const lookup = painterWarpLookup(pp.residualWarp, pp.frame.principalPx);

  const shadeRatios = buildSurfaceShadeRatios(palette);
  const { shadeLow, shadeHigh, shadeChroma } = createShadeUniformArrays(shadeRatios);

  const ditchColor = palette.ditchLines.median.linear;
  const highlightColor = palette.highlightWhite.median.linear;
  const fieldTexture = fieldTextureColours(palette);

  return {
    uPainterWarpLookup: { value: lookup.texture },
    uPainterWarpGrid: { value: lookup.grid },
    uHazeColor: { value: new Vector3(atm.hazeColor[0], atm.hazeColor[1], atm.hazeColor[2]) },
    uFogDensity: { value: atm.density },
    uFogExponent: { value: atm.exponent },
    uFogHeightFalloff: { value: atm.heightFalloff },
    uFogMaxOpacity: { value: atm.maxOpacity },
    uFogPainterExponent: { value: atm.painterExponent },
    uFogCalibrationM: { value: atm.calibrationDistanceM },
    uSkyStops: { value: skyStops },
    uSkyElevations: { value: skyElevations },
    uSkyStopCount: { value: sky.stops.length },
    uSunDirection: {
      value: new Vector3(
        light.sunDirection[0],
        light.sunDirection[1],
        light.sunDirection[2]
      ).normalize(),
    },
    uWrap: { value: light.wrap },
    uBandCount: { value: light.bandCount },
    uBandSoftness: { value: light.bandSoftness },
    uSkyDepthSentinel: { value: WORLD.skyDepthSentinel },
    uTime: { value: 0 },
    uPainterAmount: { value: 0 },
    uCitizenPoses: { value: null },
    uPainterGroundPoint: {
      value: new Vector3(pp.groundPoint[0], pp.groundPoint[1], pp.groundPoint[2]),
    },
    uPainterD0: { value: pp.d0 },
    uPainterL: { value: pp.L },
    uPainterFarKneeM: { value: pp.farKneeM },
    uPainterFarKneeShare: { value: pp.farKneeShare },
    uPainterFarScaleM: { value: pp.farScaleM },
    uPainterFarShear: { value: pp.farShear },
    uVerticalExaggeration: { value: pp.verticalExaggeration },
    uPainterCameraHeight: { value: pp.cameraHeight },
    uPainterCameraPos: { value: new Vector3(0, 0, 0) },
    uPainterForwardDir: {
      value: new Vector3(pp.forwardDir[0], pp.forwardDir[1], pp.forwardDir[2]),
    },
    uPainterRightDir: {
      value: new Vector3(pp.rightDir[0], pp.rightDir[1], pp.rightDir[2]),
    },
    uPainterHorizonDepressionRad: { value: pp.horizonDepressionRad },
    uPainterHorizonElevationTop: { value: pp.horizonElevationTop },
    ...painterWarpUniforms(pp),
    ...painterStrokeUniforms(pp),
    uShadeLow: { value: shadeLow },
    uShadeHigh: { value: shadeHigh },
    uShadeChroma: { value: shadeChroma },
    uBrushStrength: { value: brush.strength },
    uBrushLengthM: { value: brush.strokeLengthM },
    uBrushWidthM: { value: brush.strokeWidthM },
    uDitchColor: { value: new Vector3(ditchColor[0], ditchColor[1], ditchColor[2]) },
    uFieldToneA: { value: vector3(fieldTexture.toneA) },
    uFieldToneB: { value: vector3(fieldTexture.toneB) },
    uFieldStreak: { value: vector3(fieldTexture.streak) },
    ...fieldColourUniforms(palette, loadFieldColourMap()),
    uHighlightColor: {
      value: new Vector3(highlightColor[0], highlightColor[1], highlightColor[2]),
    },
    /* The drawing buffer's height, which resize.ts sets on every resize before a frame is drawn;
       the 1080p reference until then. */
    uRenderHeight: { value: 1080 },
    uCloudColor: { value: new Vector3(sky.cloudColor[0], sky.cloudColor[1], sky.cloudColor[2]) },
    uCloudCoverage: { value: sky.cloudCoverage },
    uCloudScale: { value: sky.cloudScale },
  };
}

function vector3(v: readonly [number, number, number]): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}

/* Centres go to the GPU already divided by the spline scale, in the warp texture, so the vertex
   shader does the same arithmetic as painterWarp.residualShiftPx. */
function painterWarpUniforms(pp: StyleParams['painterProjection']) {
  const { frame, residualWarp: warp } = pp;
  return {
    uPainterEye: { value: vector3(frame.eye) },
    uPainterAxisRight: { value: vector3(frame.right) },
    uPainterAxisUp: { value: vector3(frame.up) },
    uPainterAxisForward: { value: vector3(frame.forward) },
    uPainterFocalPx: { value: frame.focalPx },
    uPainterPrincipalPx: { value: new Vector2(frame.principalPx[0], frame.principalPx[1]) },
    uPainterWarpInvScale: { value: 1 / warp.scalePx },
    uPainterWarpAffine: { value: vector3(warp.affine) },
    uPainterWarpField: { value: painterWarpTexture(warp) },
    uPainterWarpFadeDepthM: { value: WARP_FADE_DEPTH_M },
    uPainterWarpLimitPx: { value: WARP_LIMIT_PX },
    ...painterVerticalWarpUniforms(warp.vertical),
    uWallFarShare: { value: WALL_EXAGGERATION.farShare },
    uWallFacingFrom: { value: WALL_EXAGGERATION.facingFrom },
    uWallFacingTo: { value: WALL_EXAGGERATION.facingTo },
  };
}

/* The column shift's centres are in the warp texture's second row, divided by its scale too, as
   painterWarp.verticalShiftPx does the arithmetic. */
function painterVerticalWarpUniforms(field: VerticalShiftField | undefined) {
  if (!field) return {};
  return {
    uPainterWarpVerticalInvScale: { value: 1 / field.scalePx },
    uPainterWarpVerticalHeldRowPx: { value: field.heldRowPx },
    uPainterWarpVerticalLastFittedRowPx: { value: field.lastFittedRowPx },
    uPainterWarpVerticalAffine: { value: vector3(field.affine) },
  };
}

function painterStrokeUniforms(pp: StyleParams['painterProjection']) {
  const data = painterStrokeUniformData(pp.strokes);
  const vectors = (rows: readonly (readonly [number, number, number, number])[]) =>
    rows.map((r) => new Vector4(r[0], r[1], r[2], r[3]));
  return {
    uPainterStrokeBoxes: { value: vectors(data.boxes) },
    uPainterStrokeValues: { value: vectors(data.values) },
    uPainterStrokeVertices: { value: vectors(data.vertexPairs) },
  };
}

/*
 * The shaders loop over the warp texture's centres to a compile-time bound, so each spline's centre
 * count reaches them as a define rather than a uniform. The column shift's count is defined only
 * for a warp with one, so without one the shaders compile to exactly the program of the row shift
 * alone.
 */
export function painterWarpDefines(uniforms: SharedUniforms): Record<string, number> {
  const { row, column } = painterWarpCounts(uniforms.uPainterWarpField.value);
  return {
    PAINTER_WARP_CENTRES: row,
    ...(column === undefined ? {} : { PAINTER_WARP_VERTICAL_CENTRES: column }),
  };
}

/* Sized as the warp's are; with no painted polder the strokes compile away (fields.glsl). */
export function painterStrokeDefines(uniforms: SharedUniforms): Record<string, number> {
  return {
    PAINTER_STROKE_REGIONS: uniforms.uPainterStrokeBoxes.value.length,
    PAINTER_STROKE_VERTEX_PAIRS: uniforms.uPainterStrokeVertices.value.length,
  };
}

/* The shade arrays are sized like the warp's: the define comes from the arrays themselves. */
export function surfaceDefines(uniforms: SharedUniforms): Record<string, number> {
  return { SURFACE_COUNT: uniforms.uShadeLow.value.length };
}

export function updateSharedUniforms(uniforms: SharedUniforms, timeSeconds: number): void {
  uniforms.uTime.value = timeSeconds;
}

export function updatePainterProjection(
  uniforms: SharedUniforms,
  state: PainterProjectionState
): void {
  uniforms.uPainterAmount.value = state.amount;
  uniforms.uPainterCameraPos.value.set(
    state.cameraPosition[0],
    state.cameraPosition[1],
    state.cameraPosition[2]
  );
}
